import BigNumber from 'bignumber.js'
import { ChainIdEnum } from 'config/constants/network'
import tokens from 'config/constants/tokens'
import { IWageringCondition, IWageringConditionBigAmount } from 'config/types/bonus'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import Column from 'layout/Components/Column'
import { RowBetween } from 'layout/Components/Row'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useTokenUsdPrices } from 'state/app/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import colors from 'theme/ui/colors'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text/Text'
import { buildPluralizeText } from 'utils'
import {
  estimateFiatDecimalValues,
  formatDisplayEstimateFiat,
  isFreeSpinBonusFiatReward,
  isWagerBonusFiatReward,
} from 'utils/bonus'
import { getFullDisplayBalance } from 'utils/formatBalance'
import BonusDetailStepCard from '../BonusDetailStepCard'

interface WageringInfoSectionDetailsProps {
  bonus: (IWageringCondition & IWageringConditionBigAmount) | WageringConditionBonus
  step: number
  isActive: boolean
  isProcessing: boolean
}

const WageringInfoSectionDetails: React.FC<WageringInfoSectionDetailsProps & BoxProps> = ({
  bonus,
  step,
  isActive,
  isProcessing,
  ...props
}) => {
  const { t } = useTranslation()
  const tokenUsdPrices = useTokenUsdPrices()

  return (
    <Box {...props}>
      <BonusDetailStepCard
        isActive={isActive}
        isProcessing={isProcessing}
        cardTitle={
          <Flex flexDirection="column">
            <Text
              textTransform="uppercase"
              fontSize="10px"
              fontWeight="600"
              ml="8px"
              lineHeight="normal"
              color="textAlt1"
            >
              <Trans>Step {{ step }}</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" mb="4px" ml="8px" lineHeight="22px">
              {bonus instanceof WageringConditionBonus && bonus.isActiveMilestone ? (
                <Trans>Wager and Claim</Trans>
              ) : (
                <Trans>Wagering</Trans>
              )}
            </Text>
          </Flex>
        }
      >
        <Box borderRadius="8px" ml="8px" mt="12px">
          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Wager</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              x{bonus.wagerMultiplier}
            </Text>
          </RowBetween>

          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Min bet for Wager</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              ${bonus.minBet}{' '}
              <StyledFiatEstimated>
                {isFreeSpinBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.minBet)
                        .dividedBy(tokenUsdPrices[(bonus as any).freeSpinCurrency])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][(bonus as any)?.freeSpincurrency]),
                          BigNumber.ROUND_UP,
                        ),
                      (bonus as any).freeSpinCurrency,
                    )
                  : isWagerBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.minBet)
                        .dividedBy(tokenUsdPrices[bonus.bonusAmount.token.code])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][bonus.bonusAmount.token.code]),
                          BigNumber.ROUND_UP,
                        ),
                      bonus.bonusAmount.token.code,
                    )
                  : ''}
              </StyledFiatEstimated>
            </Text>
          </RowBetween>

          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Max bet for Wager</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              ${bonus.maxBet}{' '}
              <StyledFiatEstimated>
                {isFreeSpinBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.maxBet)
                        .dividedBy(tokenUsdPrices[(bonus as any).freeSpinCurrency])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][(bonus as any)?.freeSpincurrency]),
                          BigNumber.ROUND_DOWN,
                        )
                        .toString(),
                      (bonus as any).freeSpinCurrency,
                    )
                  : isWagerBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.maxBet)
                        .dividedBy(tokenUsdPrices[bonus.bonusAmount.token.code])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][bonus.bonusAmount.token.code]),
                          BigNumber.ROUND_DOWN,
                        )
                        .toString(),
                      bonus.bonusAmount.token.code,
                    )
                  : ''}
              </StyledFiatEstimated>
            </Text>
          </RowBetween>

          <RowBetween>
            <Flex>
              <Text fontSize="14px" fontWeight="300" color="textSubtle">
                <Trans>Wager Duration</Trans>
              </Text>
              <Box
                data-tooltip-id="common-tip-bottom"
                data-tooltip-content={t('The time window to complete the bonus wagering')}
                ml={2}
                height="16px"
              >
                <Icons.InfoCircleIcon fill={colors.textDisable} />
              </Box>
            </Flex>

            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              {t(
                buildPluralizeText('{{wagerDuration}} {{day}}', [
                  { number: bonus.wagerDurationInDays, key: 'day', word: ['day', 'days'] },
                ]),
                { wagerDuration: bonus.wagerDurationInDays },
              )}
            </Text>
          </RowBetween>

          {bonus instanceof WageringConditionBonus &&
            bonus.isActiveMilestone &&
            bonus.milestones.map((item, index) => {
              return (
                !!item.bonusAmount.toNumber() && (
                  <Column>
                    <RowBetween mt={!index ? '16px' : '0px'} mb={!item.isClaimed ? '16px' : '0px'}>
                      <Text fontSize="14px" fontWeight="300" color="textSubtle">
                        {index === bonus.milestones.length - 1 ? (
                          <Trans>Finish</Trans>
                        ) : (
                          <Trans>Milestone {{ milestonePoint: index + 1 }}</Trans>
                        )}
                      </Text>
                      <Flex justifyContent={'flex-end'}>
                        <Text fontSize="14px" fontWeight="600" color="textTertiary">
                          {getFullDisplayBalance(item.bonusAmount, 0, 5)} {bonus.bonusAmount?.token?.name}
                        </Text>
                        {isFreeSpinBonusFiatReward(bonus) &&
                          bonus.bonusAmount?.token?.code !== (bonus as any)?.freeSpinCurrency && (
                            <StyledFiatEstimated ml="2px">
                              {formatDisplayEstimateFiat(
                                getFullDisplayBalance(
                                  new BigNumber(item.bonusAmount).dividedBy(
                                    tokenUsdPrices[(bonus as any).freeSpinCurrency],
                                  ),
                                  0,
                                ),
                                (bonus as any).freeSpinCurrency,
                              )}
                            </StyledFiatEstimated>
                          )}
                      </Flex>
                    </RowBetween>

                    {item.isClaimed && (
                      <Text fontSize="12px" bold color="success" style={{ alignSelf: 'flex-end' }} mb="16px" mt="8px">
                        <Trans>Claimed</Trans>
                      </Text>
                    )}
                  </Column>
                )
              )
            })}
        </Box>
      </BonusDetailStepCard>
    </Box>
  )
}

const StyledFiatEstimated = styled(Text).attrs({ as: 'span' })`
  color: ${({ theme: { colors } }) => colors.textDisable};
  font-size: inherit;
  font-weight: inherit;
`

export default WageringInfoSectionDetails
